import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const BG = "https://images.unsplash.com/photo-1527826507412-72e447368aa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxtYXJzJTIwdGVycmFpbiUyMHNhdGVsbGl0ZSUyMHRvcG9ncmFwaHklMjByZWQlMjBwbGFuZXQlMjBzdXJmYWNlfGVufDB8fHx8MTc5MTA1Mjk4OXww&ixlib=rb-4.1.0&q=85";

export default function Register() {
    const { register, user } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user && user !== false) navigate("/app");
    }, [user, navigate]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await register(name, email, password);
        setLoading(false);
        if (res.ok) navigate("/app");
        else setError(res.error);
    };

    return (
        <div className="min-h-screen flex bg-ares-void">
            <div className="flex-1 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-sm fade-up">
                    <Link to="/" className="flex items-center gap-2.5 mb-10" data-testid="brand-logo">
                        <BrandLogo />
                        <span className="font-heading font-bold tracking-tight">GeoMorphology</span>
                    </Link>

                    <h1 className="font-heading text-2xl font-semibold mb-1">Request access</h1>
                    <p className="text-sm text-ares-muted mb-8">Create your analyst console account.</p>

                    {error && (
                        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 mb-5" data-testid="register-error">
                            <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                            <p className="text-sm text-destructive">{error}</p>
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="name" className="text-ares-muted font-mono text-xs uppercase tracking-wider">Analyst name</Label>
                            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
                                className="bg-ares-dark border-ares-border focus:ring-ares-orange h-11" placeholder="Dr. Ava Chen" data-testid="register-name-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-ares-muted font-mono text-xs uppercase tracking-wider">Email</Label>
                            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                                className="bg-ares-dark border-ares-border focus:ring-ares-orange h-11" placeholder="analyst@ares.mars" data-testid="register-email-input" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password" className="text-ares-muted font-mono text-xs uppercase tracking-wider">Password</Label>
                            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                                className="bg-ares-dark border-ares-border focus:ring-ares-orange h-11" placeholder="min. 6 characters" data-testid="register-password-input" />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full h-11 bg-ares-orange hover:bg-[#e55a2b] text-white" data-testid="register-submit-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}
                        </Button>
                    </form>

                    <p className="mt-6 text-sm text-ares-muted text-center">
                        Already enrolled?{" "}
                        <Link to="/login" className="text-ares-orange hover:underline" data-testid="goto-login-link">Log in</Link>
                    </p>
                </div>
            </div>

            <div className="hidden lg:block lg:w-1/2 relative">
                <img src={BG} alt="Mars terrain" className="w-full h-full object-cover opacity-50" />
                <div className="absolute inset-0 bg-gradient-to-t from-ares-void via-transparent to-ares-void/60" />
                <div className="absolute inset-0 radar-grid" />
                <div className="absolute bottom-10 left-10 right-10">
                    <p className="font-mono text-xs uppercase tracking-widest text-ares-cyan mb-2">Join the Mission</p>
                    <h2 className="font-heading text-3xl font-bold text-ares-bright">Build a portfolio of<br />real Mars analysis.</h2>
                </div>
            </div>
        </div>
    );
}
